import { tv } from 'tailwind-variants';
import { cn } from '@/app_lib/utils/utils';

type AllowedInputType =
  | 'button'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'month'
  | 'number'
  | 'password'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

interface InputV2Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  id: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string;
  className?: string;
  type?: AllowedInputType;
}

const inputVariants = tv({
  base: 'border rounded-sm stroke-helper-2 active:stroke-accent',
  defaultVariants: {
    size: 'md',
  },
  variants: {
    size: {
      lg: 'typography-default input-p-lg',
      md: 'typography-sm input-p-md',
      sm: 'typography-xs input-p-sm',
    },
    variant: {
      disabled: 'button-secondary-disabled cursor-not-allowed text-helper',
      error: 'stroke-red',
    },
  },
});

const labelVariants = tv({
  base: 'flex gap-xs typography-sm',
  variants: {
    variant: {
      error: 'text-red',
    },
  },
});

export const InputV2 = ({
  label,
  id,
  value,
  onChange,
  placeholder,
  error,
  required,
  className,
  disabled = false,
  type = 'text',
  ...rest
}: InputV2Props) => {
  const inputVariant = (disabled && 'disabled') || (error && 'error') || undefined;

  return (
    <div className={cn('flex flex-col gap-xs', className)}>
      <label
        className={cn(labelVariants({ variant: error ? 'error' : undefined }), '')}
        htmlFor={id}
      >
        {label}
        {required && (
          <span
            aria-hidden='true'
            className='icon-red'
          >
            *
          </span>
        )}
      </label>
      <input
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={Boolean(error)}
        className={cn(inputVariants({ variant: inputVariant }), '')}
        disabled={disabled}
        id={id}
        name={id}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        type={type}
        {...(value !== undefined ? { value } : {})}
        {...rest}
      />
      {error && (
        <p
          className='text-red typography-xs'
          id={`${id}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};
